import React, {useCallback, useContext, useEffect, useState} from 'react';
import axios from 'axios';
import moment from 'moment';
import Spinner from 'react-bootstrap/Spinner';
import {Link, useNavigate, useParams} from 'react-router-dom';
import withAuth from '../../auth';
import {API_URL} from '../../constants';
import {
    formatPickupDropoffDisplay,
    formatPickupOriginDisplay,
} from '../../utils/locationDisplay';
import {AuthContext} from '../AuthContext';
import WhitelabelBadge from '../WhitelabelBadge';
import {getInvoiceErrorMessage} from './InvoiceList';
import {
    closeInvoicePdfWindow,
    navigateInvoicePdfWindow,
    openInvoicePdfWindow,
} from './invoicePdfWindow';
import {AXIOS_NOT_FOUND_REDIRECT_OPT_OUT} from '../../axiosNotFoundRedirect';

export const InvoiceDetails = () => {
    const {invoiceId} = useParams();
    const navigate = useNavigate();
    const {token, setToken, hasPermission} = useContext(AuthContext);
    const canMarkPaid = Boolean(hasPermission?.('pickup-update'));
    const canViewCustomers = Boolean(hasPermission?.('user-view'));
    const canViewPickups = Boolean(hasPermission?.('pickup-view'));

    const [invoice, setInvoice] = useState(null);
    const [loading, setLoading] = useState(true);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    const [isMarkPaidSubmitted, setIsMarkPaidSubmitted] = useState(false);
    const [paidMethod, setPaidMethod] = useState('EFT');
    const [paidReference, setPaidReference] = useState('');

    const fetchInvoice = useCallback(async () => {
        setLoading(true);
        setError('');

        try {
            const response = await axios.get(
                `${API_URL}/api/admin/invoices/${invoiceId}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            setInvoice(response.data.data || null);
        } catch (requestError) {
            setInvoice(null);

            if (requestError?.response?.status === 401) {
                localStorage.removeItem('authToken');
                setToken(null);
                navigate('/');
            } else {
                setError(getInvoiceErrorMessage(
                    requestError,
                    'An error occurred while loading the invoice. Please try again later.'
                ));
            }
        } finally {
            setLoading(false);
        }
    }, [invoiceId, navigate, setToken, token]);

    useEffect(() => {
        fetchInvoice();
    }, [fetchInvoice]);

    const openInvoicePdf = async () => {
        setError('');
        const pdfWindow = openInvoicePdfWindow();

        if (!pdfWindow) {
            setError('Unable to open the invoice PDF. Please allow pop-ups and try again.');
            return;
        }

        try {
            const response = await axios.get(
                `${API_URL}/api/admin/invoices/${invoice.id}/pdf`,
                {
                    ...AXIOS_NOT_FOUND_REDIRECT_OPT_OUT,
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (!navigateInvoicePdfWindow(pdfWindow, response.data?.url)) {
                closeInvoicePdfWindow(pdfWindow);
                setError('Invoice PDF not found.');
            }
        } catch (requestError) {
            closeInvoicePdfWindow(pdfWindow);

            if (requestError?.response?.status === 401) {
                localStorage.removeItem('authToken');
                setToken(null);
                navigate('/');
            } else {
                setError(getInvoiceErrorMessage(requestError, 'Failed to fetch the invoice PDF.'));
            }
        }
    };

    const openReceiptPdf = async () => {
        setError('');
        const pdfWindow = openInvoicePdfWindow('receipt');

        if (!pdfWindow) {
            setError('Unable to open the receipt PDF. Please allow pop-ups and try again.');
            return;
        }

        try {
            const response = await axios.get(
                `${API_URL}/api/admin/invoices/${invoice.id}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (!navigateInvoicePdfWindow(
                pdfWindow,
                response.data?.data?.receipt?.pdf_url
            )) {
                closeInvoicePdfWindow(pdfWindow);
                setError('Receipt PDF not found.');
            }
        } catch (requestError) {
            closeInvoicePdfWindow(pdfWindow);

            if (requestError?.response?.status === 401) {
                localStorage.removeItem('authToken');
                setToken(null);
                navigate('/');
            } else {
                setError(getInvoiceErrorMessage(requestError, 'Failed to fetch the receipt PDF.'));
            }
        }
    };

    const markInvoicePaid = async () => {
        if (!window.confirm('Mark this invoice as paid?')) return;

        setIsMarkPaidSubmitted(true);
        setSuccess('');
        setError('');

        try {
            const response = await axios.post(
                `${API_URL}/api/admin/invoices/${invoice.id}/mark-paid`,
                {
                    paid_method: paidMethod,
                    paid_reference: paidReference.trim() || null,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            setSuccess(response.data.message || 'Invoice marked as paid.');
            await fetchInvoice();
        } catch (requestError) {
            setError(getInvoiceErrorMessage(requestError, 'Failed to mark invoice as paid.'));
        } finally {
            setIsMarkPaidSubmitted(false);
        }
    };

    const statusColors = {
        open: '#FFF4CC',
        paid: '#D1F2CF',
        void: '#FFD6D6',
    };

    if (loading) {
        return (
            <div
                className="d-flex justify-content-center align-items-center"
                style={{height: '80vh'}}
            >
                <div className="spinner-border" role="status">
                    <span className="sr-only">Loading...</span>
                </div>
            </div>
        );
    }

    if (!invoice) {
        return (
            <div className="container mt-3">
                {error && <div className="alert alert-danger" role="alert">{error}</div>}
                <div>Invoice not found.</div>
            </div>
        );
    }

    return (
        <div className="container-fluid mt-3">
            <h2 className="text-center">Invoice Details #{invoice.invoice_number}</h2>

            {success && <div className="alert alert-success" role="alert">{success}</div>}
            {error && <div className="alert alert-danger" role="alert">{error}</div>}

            <div className="row">
                <div className="col-md-6">
                    <div className="card mb-3">
                        <div className="card-body text-center">
                            <h5
                                className="p-2"
                                style={{backgroundColor: statusColors[invoice.status] || 'transparent'}}
                            >
                                Status: {invoice.status}
                            </h5>

                            <p>
                                <strong>Invoice #:</strong> {invoice.invoice_number}
                                <br/>
                                <strong>Whitelabel:</strong>{' '}
                                <WhitelabelBadge
                                    whitelabelId={invoice.whitelabel_id}
                                    whitelabel={invoice.whitelabel}
                                />
                                <br/>
                                <strong>Issue Date:</strong>{' '}
                                {invoice.issue_date
                                    ? moment(invoice.issue_date).format('YYYY-MM-DD')
                                    : '—'}
                                <br/>
                                <strong>Due Date:</strong>{' '}
                                {invoice.due_date
                                    ? moment(invoice.due_date).format('YYYY-MM-DD')
                                    : '—'}
                                <br/>
                                <strong>Billing Period:</strong>{' '}
                                {invoice.billing_period_start && invoice.billing_period_end
                                    ? `${moment(invoice.billing_period_start).format('YYYY-MM-DD')} → ${moment(
                                        invoice.billing_period_end
                                    ).format('YYYY-MM-DD')}`
                                    : '—'}
                                <br/>
                                <strong>Currency:</strong> {invoice.currency || 'CAD'}
                                <br/>
                                <strong>Total:</strong> {invoice.total}
                            </p>

                            {invoice.user && (
                                <p>
                                    <strong>Customer:</strong>{' '}
                                    {canViewCustomers ? (
                                        <Link to={`/users/${invoice.user.id}`}>
                                            {invoice.user.id} {invoice.user.full_name}
                                        </Link>
                                    ) : (
                                        <span>{invoice.user.id} {invoice.user.full_name}</span>
                                    )}
                                </p>
                            )}

                            <div className="d-flex justify-content-center flex-wrap">
                                <button className="btn btn-primary mr-2 mb-2" onClick={openInvoicePdf}>
                                    Download Invoice
                                </button>

                                {invoice.receipt?.pdf_url && (
                                    <button
                                        className="btn btn-outline-success mb-2"
                                        onClick={openReceiptPdf}
                                    >
                                        Download Receipt
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {canMarkPaid
                        && invoice.source !== 'card_payment'
                        && invoice.status !== 'paid'
                        && invoice.status !== 'void' && (
                        <div className="card mb-3">
                            <div className="card-body">
                                <h5 className="text-center">Mark Invoice Paid</h5>

                                <div className="mb-3">
                                    <label className="form-label" htmlFor="invoice-paid-method">
                                        Payment Method
                                    </label>
                                    <select
                                        id="invoice-paid-method"
                                        className="form-control"
                                        value={paidMethod}
                                        onChange={(event) => setPaidMethod(event.target.value)}
                                    >
                                        <option value="EFT">EFT</option>
                                        <option value="Wire">Wire</option>
                                        <option value="Cash">Cash</option>
                                        <option value="Cheque">Cheque</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label" htmlFor="invoice-paid-reference">
                                        Reference
                                    </label>
                                    <input
                                        id="invoice-paid-reference"
                                        type="text"
                                        className="form-control"
                                        value={paidReference}
                                        onChange={(event) => setPaidReference(event.target.value)}
                                        placeholder="Optional reference"
                                    />
                                </div>

                                <button
                                    className="btn btn-success"
                                    onClick={markInvoicePaid}
                                    disabled={isMarkPaidSubmitted}
                                >
                                    {isMarkPaidSubmitted ? (
                                        <>
                                            <Spinner
                                                as="span"
                                                animation="border"
                                                size="sm"
                                                role="status"
                                                aria-hidden="true"
                                            />
                                            &nbsp;Marking Paid...
                                        </>
                                    ) : (
                                        'Mark as Paid'
                                    )}
                                </button>
                            </div>
                        </div>
                    )}

                    {invoice.status === 'paid' && (
                        <div className="card mb-3">
                            <div className="card-body">
                                <h5 className="text-center">Payment Details</h5>
                                <p className="mb-0">
                                    <strong>Paid At:</strong>{' '}
                                    {invoice.paid_at
                                        ? moment(invoice.paid_at).format('YYYY-MM-DD HH:mm:ss')
                                        : '—'}
                                    <br/>
                                    <strong>Payment Method:</strong> {invoice.paid_method || '—'}
                                    <br/>
                                    <strong>Reference:</strong> {invoice.paid_reference || '—'}
                                    <br/>
                                    <strong>Receipt #:</strong>{' '}
                                    {invoice.receipt?.receipt_number || '—'}
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                <div className="col-md-6">
                    <div className="card mb-3">
                        <div className="card-header">
                            <h5 className="mb-0 text-center">Totals</h5>
                        </div>
                        <div className="card-body">
                            <table className="table table-sm table-striped text-center mb-0">
                                <tbody>
                                <tr><td>Subtotal</td><td>{invoice.subtotal}</td></tr>
                                <tr><td>GST</td><td>{invoice.tax_gst}</td></tr>
                                <tr><td>PST</td><td>{invoice.tax_pst}</td></tr>
                                <tr><td>HST</td><td>{invoice.tax_hst}</td></tr>
                                <tr><td>QST</td><td>{invoice.tax_qst}</td></tr>
                                <tr><td>Discount</td><td>{invoice.discount_amount}</td></tr>
                                <tr>
                                    <td><strong>Total</strong></td>
                                    <td><strong>{invoice.currency || 'CAD'} {invoice.total}</strong></td>
                                </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card mb-3">
                <div className="card-header">
                    <h5 className="mb-0 text-center">Invoice Items</h5>
                </div>
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-sm table-striped text-center mb-0">
                            <thead>
                            <tr>
                                <th>Delivery</th>
                                <th>Route</th>
                                <th>Completed</th>
                                <th>Driver</th>
                                <th>Subtotal</th>
                                <th>Tax</th>
                                <th>Discount</th>
                                <th>Total</th>
                            </tr>
                            </thead>
                            <tbody>
                            {(invoice.items || []).map((item) => {
                                const pickup = item.pickup;
                                const pickupId = pickup?.id ?? item.pickup_id;
                                const canLinkPickup = Boolean(
                                    pickup && !pickup.deleted_at && canViewPickups
                                );
                                const meta = item.meta || {};
                                const pickupForDisplay = {
                                    ...(pickup || {}),
                                    origin_address: pickup?.origin_address || meta.origin_address,
                                    dropoff_address: pickup?.dropoff_address || meta.dropoff_address,
                                };
                                const route = [
                                    formatPickupOriginDisplay(pickupForDisplay),
                                    formatPickupDropoffDisplay(pickupForDisplay),
                                ].filter(Boolean).join(' → ');
                                const taxTotal = ['tax_gst', 'tax_pst', 'tax_hst', 'tax_qst']
                                    .reduce((total, key) => total + Number.parseFloat(item[key] || 0), 0);

                                return (
                                    <tr key={item.id}>
                                        <td>
                                            {pickupId ? (canLinkPickup
                                                ? <Link to={`/pickups/${pickupId}`}>#{pickupId}</Link>
                                                : `#${pickupId}`
                                            ) : '—'}
                                        </td>
                                        <td>{route || item.description || '—'}</td>
                                        <td>
                                            {meta.completed_at || pickup?.completed_at
                                                ? moment(meta.completed_at || pickup.completed_at).format('YYYY-MM-DD')
                                                : '—'}
                                        </td>
                                        <td>
                                            {meta.driver_name
                                                || pickup?.driver?.full_name
                                                || pickup?.driver?.name
                                                || '—'}
                                        </td>
                                        <td>{item.line_subtotal}</td>
                                        <td>{taxTotal.toFixed(2)}</td>
                                        <td>{item.discount_amount}</td>
                                        <td>{item.line_total}</td>
                                    </tr>
                                );
                            })}

                            {(!invoice.items || invoice.items.length === 0) && (
                                <tr>
                                    <td colSpan="8" className="py-4 text-muted">
                                        No invoice items found.
                                    </td>
                                </tr>
                            )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default withAuth(InvoiceDetails);
